import type { CardRecord } from '../shared/types';
import type { IdentityReviewGroupSummary, IdentityReviewPreviewCard } from '../catalog/catalog-api';
import type { StorageEntityDetailResponse } from '../storage/storage-api';

export const IDENTITY_REVIEW_RESULT_LIMITS = [5, 10, 25, 50] as const;
export type IdentityReviewResultLimit = (typeof IDENTITY_REVIEW_RESULT_LIMITS)[number];
export const DEFAULT_IDENTITY_REVIEW_RESULT_LIMIT: IdentityReviewResultLimit = 5;
export const DEFAULT_IDENTITY_REVIEW_SORT_ORDER = 'newest' as const;
export type IdentityReviewSortOrder = 'newest' | 'oldest';
const IDENTITY_REVIEW_RESULT_LIMIT_STORAGE_PREFIX = 'tcvs:reviews:identity-result-limit';

type ResultLimitStorage = Pick<Storage, 'getItem' | 'setItem'>;

export function browserResultLimitStorage(): ResultLimitStorage | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

export function identityReviewResultLimitPreferenceKey(reviewerId?: string): string {
  const owner = reviewerId?.trim() || 'browser';
  return `${IDENTITY_REVIEW_RESULT_LIMIT_STORAGE_PREFIX}:${encodeURIComponent(owner)}`;
}

export function readIdentityReviewResultLimitPreference(
  storage: Pick<ResultLimitStorage, 'getItem'> | undefined,
  reviewerId?: string,
): IdentityReviewResultLimit {
  if (storage === undefined) return DEFAULT_IDENTITY_REVIEW_RESULT_LIMIT;
  try {
    const value = Number(storage.getItem(identityReviewResultLimitPreferenceKey(reviewerId)));
    return isResultLimit(value) ? value : DEFAULT_IDENTITY_REVIEW_RESULT_LIMIT;
  } catch {
    return DEFAULT_IDENTITY_REVIEW_RESULT_LIMIT;
  }
}

export function writeIdentityReviewResultLimitPreference(
  storage: Pick<ResultLimitStorage, 'setItem'> | undefined,
  reviewerId: string | undefined,
  resultLimit: IdentityReviewResultLimit,
): void {
  if (storage === undefined) return;
  try {
    storage.setItem(identityReviewResultLimitPreferenceKey(reviewerId), String(resultLimit));
  } catch {
    // Storage can be unavailable in privacy-restricted browser contexts.
  }
}

/** Serializable Identity Reviews list state carried through the batch workbench route. */
export interface IdentityReviewListSnapshot {
  groups: IdentityReviewGroupSummary[];
  loadedCards: Array<[string, CardRecord[]]>;
  expandedGroupKeys: string[];
  previewItems: Array<[string, IdentityReviewPreviewCard[]]>;
  batchDetails: Array<[string, StorageEntityDetailResponse | null]>;
  resultLimit: IdentityReviewResultLimit;
  visibleCount: number;
  hideIdentityReviewed: boolean;
  hideImageReviewed: boolean;
  showInProgress: boolean;
  showCompleted: boolean;
  searchDraft: string;
  searchQuery: string;
  sortOrder: IdentityReviewSortOrder;
  scrollY: number;
}

export interface ReviewListReturnContext {
  identityReviewListSnapshot: IdentityReviewListSnapshot;
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function isJsonValue(value: unknown, seen = new WeakSet<object>()): boolean {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return true;
  if (typeof value === 'number') return Number.isFinite(value);
  if (Array.isArray(value)) return value.every((item) => isJsonValue(item, seen));
  if (!isPlainRecord(value) || seen.has(value)) return false;
  seen.add(value);
  return Object.values(value).every((item) => isJsonValue(item, seen));
}

function isResultLimit(value: unknown): value is IdentityReviewResultLimit {
  return (
    typeof value === 'number' &&
    IDENTITY_REVIEW_RESULT_LIMITS.includes(value as IdentityReviewResultLimit)
  );
}

function isSortOrder(value: unknown): value is IdentityReviewSortOrder {
  return value === 'newest' || value === 'oldest';
}

function isStringPairArray(value: unknown): value is Array<[string, unknown]> {
  return (
    Array.isArray(value) &&
    value.every(
      (entry) =>
        Array.isArray(entry) &&
        entry.length === 2 &&
        typeof entry[0] === 'string' &&
        isJsonValue(entry[1]),
    )
  );
}

function identityReviewListSnapshotFrom(value: unknown): IdentityReviewListSnapshot | undefined {
  if (!isPlainRecord(value)) return undefined;
  if (
    !Array.isArray(value.groups) ||
    !isStringPairArray(value.loadedCards) ||
    !Array.isArray(value.expandedGroupKeys) ||
    !value.expandedGroupKeys.every((key) => typeof key === 'string') ||
    !isStringPairArray(value.previewItems) ||
    !isStringPairArray(value.batchDetails) ||
    !isResultLimit(value.resultLimit) ||
    typeof value.visibleCount !== 'number' ||
    !Number.isInteger(value.visibleCount) ||
    value.visibleCount < 0 ||
    typeof value.hideIdentityReviewed !== 'boolean' ||
    typeof value.hideImageReviewed !== 'boolean' ||
    typeof value.showInProgress !== 'boolean' ||
    typeof value.showCompleted !== 'boolean' ||
    typeof value.searchDraft !== 'string' ||
    typeof value.searchQuery !== 'string' ||
    (value.sortOrder !== undefined && !isSortOrder(value.sortOrder)) ||
    typeof value.scrollY !== 'number' ||
    !Number.isFinite(value.scrollY) ||
    value.scrollY < 0 ||
    !isJsonValue(value.groups)
  ) {
    return undefined;
  }
  return {
    ...(value as unknown as IdentityReviewListSnapshot),
    sortOrder: isSortOrder(value.sortOrder) ? value.sortOrder : DEFAULT_IDENTITY_REVIEW_SORT_ORDER,
  };
}

export function reviewListReturnContext(
  snapshot: IdentityReviewListSnapshot,
): ReviewListReturnContext | undefined {
  return reviewListReturnContextFrom({ identityReviewListSnapshot: snapshot });
}

/** Parse router state defensively; direct and stale links simply start a fresh Reviews list. */
export function reviewListReturnContextFrom(state: unknown): ReviewListReturnContext | undefined {
  if (!isPlainRecord(state)) return undefined;
  const snapshot = identityReviewListSnapshotFrom(state.identityReviewListSnapshot);
  return snapshot === undefined ? undefined : { identityReviewListSnapshot: snapshot };
}
